import { getSurveysOptions, getSurveyOptions } from "@pulse/api/queryApi";
import { softDeleteSurvey } from "@pulse/api/surveyApi";
import { queryClient } from "@pulse/queryClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import ActionButton from "./ActionButton";
import { useNavigate } from "@tanstack/react-router";
import { useSelectedCustomerIdParam } from "@/features/hub/selectedCustomerContext";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "frontend-components";
import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";

export default function DeleteSurvey({ surveyId }: { surveyId: number }) {
    const { data: survey } = useQuery(getSurveyOptions({ surveyId }));
    const { t } = useTranslation();
    const customerId = useSelectedCustomerIdParam();
    const navigate = useNavigate({ from: "/$customerId/pulse/$pulseId/surveys/$surveyId" });
    const deleteMutation = useMutation({
        mutationFn: () => softDeleteSurvey({ surveyId: surveyId }),
        onSuccess: () => {
            navigate({
                to: "/$customerId/pulse/$pulseId",
                params: { customerId, pulseId: String(survey?.pulseInstanceId ?? "") },
            });
            queryClient.invalidateQueries(getSurveyOptions({ surveyId: surveyId }));
            queryClient.invalidateQueries(
                getSurveysOptions({ pulseInstanceId: survey?.pulseInstanceId || 0 }),
            );
        },
    });
    return (
        <AlertDialog>
            <AlertDialogTrigger
                render={
                    <ActionButton label={t("responseOverview.deleteSurvey")}>
                        <FontAwesomeIcon icon={faTrashCan} size="xl" />
                    </ActionButton>
                }
            />
            <AlertDialogContent size="sm">
                <AlertDialogHeader>
                    <FontAwesomeIcon icon={faTrashCan} className="text-error" size="4x" />

                    <AlertDialogTitle>{t("responseOverview.deleteSurveyText")}</AlertDialogTitle>

                    <AlertDialogDescription>
                        {t("responseOverview.deleteSurveyDescription")}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                {deleteMutation.isError && (
                    <div className="text-center text-error">
                        <p>{t("responseOverview.deleteSurveyError")}</p>
                    </div>
                )}
                <AlertDialogFooter>
                    <AlertDialogCancel variant="outline" disabled={deleteMutation.isPending}>
                        {t("common.cancel")}
                    </AlertDialogCancel>
                    <AlertDialogAction
                        variant="fill"
                        onClick={() => deleteMutation.mutate()}
                        disabled={deleteMutation.isPending}
                    >
                        {t("common.complete")}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
